import { AlertModal } from '@karnov/kui-react'
import { useCallback, useEffect } from 'react'

import { invalidateConversationsQuery } from 'api/conversations/invalidators'
import { useDeleteConversationMutation } from 'api/conversations/mutations'
import { Text } from 'components/Text'
import { useDebouncedSearchValue } from 'hooks/useDebouncedSearchValue'
import { useTranslation } from 'services/i18n'

import { Conversation } from 'types/Api'
import { triggerToastNotification } from 'utils/triggerToastNotification'

import type { FC } from 'react'

type Props = {
  isOpen: boolean
  conversation: Conversation | null
  onClearActiveConversationId: VoidFunction
  onClose: VoidFunction
}

export const DeleteConversationModal: FC<Props> = ({
  isOpen,
  conversation,
  onClearActiveConversationId,
  onClose,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'conversation_history',
  })

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    }
    else {
      document.body.style.overflow = ''
    }

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  const debouncedValue = useDebouncedSearchValue()

  const { mutate: deleteConversation, isPending: isDeleting }
    = useDeleteConversationMutation({
      onSuccess: () => {
        invalidateConversationsQuery(debouncedValue)
        triggerToastNotification({
          duration: 2000,
          message: t('toast_notification.conversation_delete_success'),
          variant: 'success',
        })
        onClearActiveConversationId()
        onClose()
      },
      onError: () => {
        triggerToastNotification({
          duration: 2000,
          message: t('toast_notification.conversation_delete_error'),
          variant: 'error',
        })
      },
    })

  const handleDeleteConversation = useCallback(() => {
    if (conversation) {
      deleteConversation({ id: conversation.id })
    }
  }, [conversation, deleteConversation])

  return (
    <AlertModal
      alertConfirmLabel={t('conversation_delete_modal.delete_button')}
      cancelLabel={t('conversation_delete_modal.cancel_button')}
      isConfirming={isDeleting}
      isOpen={isOpen}
      name="delete-conversation"
      onAlertConfirm={handleDeleteConversation}
      onCancel={onClose}
      onClose={onClose}
      title={t('conversation_delete_modal.title')}
    >
      <p className="pt-4">
        <Text>{t('conversation_delete_modal.question')}</Text>
        <Text className="ml-1 font-medium">{conversation?.subject}</Text>
        <Text>?</Text>
      </p>
    </AlertModal>
  )
}
